/** @jsxImportSource @emotion/react */
import { useAtomValue } from 'jotai'
import { useMemo, useCallback, useState } from 'react'
import { Alert, Box, Button, Drawer, TextField, Typography, useTheme } from '@mui/material'

import { clientNameAtom } from '@app/state/clientsAtom'
import { handleRolesCascade } from '../utils/Admin.utils'
import { useInviteAdminUsers } from '../hooks/useInviteAdminUsers'
import { SELECTABLE_ROLES, USER_ROLES_LABELS_MAP } from '@app/app-constants'
import { formatEmailsToArray, validateEmails } from '@app/utils/adminEmail.utils'

import { UserCustomRole, UserRole } from '@app/types'
import { toast } from '@app/components/ui/Toast/toast'
import { Autocomplete } from '@app/components/Atoms/Autocomplete/Autocomplete'
import {
  drawerActionsStyle,
  formDescriptionStyle,
  formHelperTextStyle,
  formInputContainerStyle,
  formTextFieldStyle,
  formTitleStyle,
  validationErrorStyle,
} from '../styles/Admin.styles'
import { LoadingSpinner } from '@app/components/ui/LoadingSpinner/LoadingSpinner'

type AdminInviteDrawerProps = {
  opensInviteDrawer: boolean
  setOpensInviteDrawer: (open: boolean) => void
  emailsToInvite: string
  setEmailsToInvite: (emails: string) => void
  customRoles: UserCustomRole[] | undefined
  customRolesLoading: boolean
}

export const AdminInviteDrawer = ({
  opensInviteDrawer,
  setOpensInviteDrawer,
  emailsToInvite,
  setEmailsToInvite,
  customRoles,
  customRolesLoading,
}: AdminInviteDrawerProps) => {
  const theme = useTheme()
  const clientName = useAtomValue(clientNameAtom)
  const inviteUsersMutation = useInviteAdminUsers()
  const [rolesForInvite, setRolesForInvite] = useState<UserRole[]>([
    UserRole.MEMBER,
    UserRole.VIEWER,
  ])

  const validationError = useMemo(() => validateEmails(emailsToInvite), [emailsToInvite])

  const handleSendInvites = useCallback(async () => {
    if (validationError || !clientName) return

    const emailArray = formatEmailsToArray(emailsToInvite)

    await inviteUsersMutation.mutateAsync(
      { emails: emailArray, clientName, roles: rolesForInvite },
      {
        onSuccess: (data) => {
          if (data.newAccounts.length > 0) {
            const successfulEmails = data.newAccounts.map((account) => account.email)
            toast({
              description: `Successfully invited ${successfulEmails.join(', ').substring(0, 250)}${
                successfulEmails.length > 250 ? '...' : ''
              }`,
              type: 'success',
              position: 'top-center',
            })
          }

          if (data.failedAccounts.length > 0) {
            toast({
              description: `Email invite failed for ${data.failedAccounts
                .join(', ')
                .substring(0, 250)}${data.failedAccounts.length > 250 ? '...' : ''}`,
              type: 'error',
              position: 'top-center',
            })
            setEmailsToInvite(data.failedAccounts.join(', '))
          } else setEmailsToInvite('')
          setRolesForInvite([UserRole.VIEWER])
        },
      },
    )
  }, [
    validationError,
    emailsToInvite,
    setEmailsToInvite,
    clientName,
    inviteUsersMutation,
    rolesForInvite,
  ])

  return (
    <Drawer
      aria-labelledby="invite-drawer-title"
      anchor="right"
      open={opensInviteDrawer}
      onClose={() => setOpensInviteDrawer(!opensInviteDrawer)}
      PaperProps={{
        sx: { width: { xs: '100%', sm: '700px' }, padding: 6 },
      }}
    >
      {customRolesLoading || inviteUsersMutation.isPending ? (
        <Box css={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <LoadingSpinner />
        </Box>
      ) : null}
      <Box>
        <Typography
          id="invite-drawer-title"
          variant="h5"
          css={formTitleStyle(theme.palette.primary.dark)}
        >
          Invite external users
        </Typography>
        <Typography variant="body1" css={formDescriptionStyle(theme.palette.primary.dark)}>
          Enter the email addresses of the users you would like to invite to this client.
        </Typography>
        <Box css={formInputContainerStyle}>
          <TextField
            css={formTextFieldStyle}
            placeholder="mia@example.com, nikau@example.com"
            name="emailsToInvite"
            aria-describedby="emailHelp"
            value={emailsToInvite}
            onChange={(e) => setEmailsToInvite(e.target.value)}
            error={!!validationError}
          />
          <Autocomplete
            value={rolesForInvite}
            options={[
              ...SELECTABLE_ROLES,
              ...(customRoles
                ? customRoles.map((role) => role.name.toLowerCase() as UserRole)
                : []),
            ]}
            onChange={(_, value) => setRolesForInvite(handleRolesCascade(value) as UserRole[])}
            labelsMap={{
              ...USER_ROLES_LABELS_MAP,
              ...(customRoles
                ? Object.fromEntries(
                    customRoles.map((role) => [role.name.toLowerCase(), role.description]),
                  )
                : {}),
            }}
            customStyles={{ width: '100%' }}
          />
        </Box>
        <Typography variant="body1" css={formHelperTextStyle(theme.palette.primary.dark)}>
          *Invites will expire after 24 hours.
        </Typography>
        {validationError && (
          <Box css={validationErrorStyle}>
            <Alert severity="error">{validationError}</Alert>
          </Box>
        )}
      </Box>
      <Box css={drawerActionsStyle}>
        <Button variant="outlined" onClick={() => setOpensInviteDrawer(false)}>
          Cancel
        </Button>
        <Button
          disabled={inviteUsersMutation.isPending}
          variant="contained"
          onClick={() => handleSendInvites()}
        >
          Send Invites
        </Button>
      </Box>
    </Drawer>
  )
}
